import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import Breadcrumb from '../../components/Breadcrumb';
import Page from '../../components/Page';
import Pagination from '../../components/Pagination';
import client from '../../lib/feathers';

const getStatusBadge = (pass) => {
  if (!pass.isActive) {
    return { label: 'Disabled', classes: 'bg-gray-100 text-gray-800' };
  }
  if (pass.expiresAt && new Date(pass.expiresAt) < new Date()) {
    return { label: 'Expired', classes: 'bg-red-100 text-red-800' };
  }
  if (pass.maxUses && pass.usedCount >= pass.maxUses) {
    return { label: 'Maxed', classes: 'bg-yellow-100 text-yellow-800' };
  }
  return { label: 'Active', classes: 'bg-green-100 text-green-800' };
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const tierLabels = {
  taster: 'Taster',
  collector: 'Collector',
  enthusiast: 'Enthusiast',
  connoisseur: 'Connoisseur',
  vip: 'VIP',
};

export default function AccessPasses() {
  const navigate = useNavigate();
  const [query] = useSearchParams();
  const limit = 25;
  const [page, setPage] = useState(query.get('page') || 0);
  const [passes, setPasses] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getPasses = async () => {
    try {
      setLoading(true);
      setError(null);
      const service = client.service('admin/access-pass');
      const result = await service.find({
        query: {
          $limit: limit,
          $skip: page * limit,
          $sort: { createdAt: -1 },
        },
      });
      setPasses(result.data);
      setTotal(result.total);
    } catch (e) {
      console.error('Error loading access passes:', e);
      setError('Failed to load access passes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const navigateToPage = (p = 0) => {
    if (p * limit > total || p * limit < 0) return;
    navigate(`/admin/access-passes?page=${p}`);
    setPage(p);
  };

  useEffect(() => {
    getPasses();
  }, [page]);

  return (
    <Page title="Access Passes">
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <nav className="flex" aria-label="Breadcrumb">
          <Breadcrumb elements={[{ name: 'Access Passes' }]} />
        </nav>
        <div className="mt-3 sm:mt-0 sm:ml-4">
          <Link
            to="/admin/access-passes/new"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-red-700"
          >
            Create Access Pass
          </Link>
        </div>
      </div>

      <div className="mt-6 pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          Access Passes ({total} Total)
        </h3>
        <div className="mt-3 sm:mt-0 sm:ml-4">
          <Pagination total={total} limit={limit} page={page} navigateToPage={navigateToPage} />
        </div>
      </div>

      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="flex flex-col mt-4">
          <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Code
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tier
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Usage
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Expires
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">View</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {passes.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-12 text-center text-sm text-gray-500">
                          No access passes found.
                        </td>
                      </tr>
                    ) : (
                      passes.map((pass) => {
                        const status = getStatusBadge(pass);
                        return (
                          <tr
                            className="hover:bg-gray-100 cursor-pointer"
                            key={pass.id}
                            onClick={() => navigate(`/admin/access-passes/${pass.id}`)}
                          >
                            <td className="px-6 py-4">
                              <div className="text-sm font-mono font-medium text-gray-900">
                                {pass.code}
                              </div>
                              {pass.description && (
                                <div className="text-sm text-gray-500 truncate max-w-xs">
                                  {pass.description}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {tierLabels[pass.grantTier] || pass.grantTier}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {pass.usedCount} / {pass.maxUses || '\u221E'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(pass.expiresAt)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${status.classes}`}>
                                {status.label}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <Link
                                to={`/admin/access-passes/${pass.id}`}
                                className="text-primary hover:text-red-900"
                                onClick={(e) => e.stopPropagation()}
                              >
                                View
                              </Link>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
                <Pagination total={total} limit={limit} page={page} navigateToPage={navigateToPage} />
              </div>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
